/**
 * SureCo Design System tokens for VCN Management.
 *
 * Source: the `colors_and_type.css` (v2.2) inside the `_ds` design-system
 * folder of
 * P'Sing's VCN Management design project — the file the design HTML actually
 * links, which supersedes the older oklch-based `colors_and_type.css` sitting
 * at that project's root. Values are already hex there, so nothing is converted.
 *
 * `ReconciliationV2/tokens.ts` carries the same palette from the same source;
 * the duplication is deliberate rather than a shared import, so one feature's
 * restyle cannot silently repaint the other.
 */

export const font = {
  sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
} as const

export const colors = {
  brand: {
    teal: '#01A0A8',
    tealHover: '#0A939B',
  },
  text: {
    default: '#112A43',
    secondary: '#334E68',
    muted: '#617D99',
    placeholder: '#839AB1',
  },
  surface: {
    white: '#FFFFFF',
    /** The design's page canvas behind the table. */
    page: '#F7F9FC',
    offWhite: '#F3F6F9',
    hover: '#F0F4F8',
  },
  border: {
    default: '#D9E2EC',
    focus: '#0A939B',
  },
  /** Literal values from `VCN Table.dc.html`, which states them directly
      rather than by token. */
  table: {
    shellBorder: '#E7EAEE',
    divider: '#ECEEF1',
    checkboxBorder: '#C7CCD1',
  },
  /** Provider badges — squarer and bolder than the status pills on purpose:
      provider is a fixed vocabulary, status is a lifecycle. */
  provider: {
    marqeta: {
      background: '#F4EFFB',
      borderColor: '#C9B8EC',
      color: '#6B4BA8',
    },
    wex: { background: '#EEF4FA', borderColor: '#B9D2E6', color: '#3A6F99' },
    /** iStream and anything else: absent from the design, but the badges must
        still agree in shape or the column looks broken. */
    neutral: {
      background: '#F4F6F8',
      borderColor: '#D9DEE4',
      color: '#5A6673',
    },
  },
} as const

/** Card status pills, from the design file's activeStyle / terminatedStyle.
    Suspended is not in the design, so it keeps the EMS-8827 amber. */
export const statusPill = {
  active: { background: '#F5FADF', borderColor: '#EBF5C1', color: '#5C6B2E' },
  terminated: {
    background: '#FCEADF',
    borderColor: '#FAD3C0',
    color: '#8A3B34',
  },
} as const

export const radius = {
  badge: 6,
  control: 6,
  shell: 12,
  checkbox: 4,
} as const

export const tableType = {
  /** The design sets the table at 15px, a step up from the app default. */
  fontSize: 15,
  headerWeight: 600,
  headerPadding: '14px 16px',
  cellPadding: 16,
} as const

/** Toolbar controls, from the design's Refresh / Export / Bulk upload / Filters
    buttons: a 42px-tall white control with a 1px border, 6px corner, and the
    icon trailing the label. Icon-only controls are square. */
export const toolbarControl = {
  height: 42,
  paddingX: 15,
  gap: 7,
  fontSize: 14,
  fontWeight: 500,
  iconSize: 16,
} as const
